import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateProvider } from "@/features/ledger/api/use-create-provider";
import { useUpdateProvider } from "@/features/ledger/api/use-update-provider";
import type { ProviderDto } from "@/features/ledger/api/dto";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import type { ProviderRow } from "@/features/ledger/components/providers/providers-columns";
import {
  createProviderSchema,
  type ProviderFormValues,
} from "@/features/ledger/schemas/provider.schema";
import { PROVIDER_STATUSES, PROVIDER_TYPES } from "@/features/shared/api/enums";

const EMPTY_VALUES: ProviderFormValues = {
  code: "",
  name: "",
  type: "INTERNAL",
  status: "ACTIVE",
};

const toPayload = (values: ProviderFormValues): ProviderDto => ({
  code: values.code.trim(),
  name: values.name.trim(),
  type: values.type,
  status: values.status,
});

/**
 * Formulaire de création / mise à jour d'un provider
 * (`POST /Providers/add`, `PUT /Providers/{id}`, guide §6.4) — réservé à
 * flashpay-superadmin / flashpay-admin. Preview ShareablePreview avant
 * submit (pas de flux d'argent → pas de section frais).
 */
export function ProviderFormDialog({
  provider,
  open,
  onOpenChange,
}: {
  /** Provider à éditer — absent = création. */
  provider?: ProviderRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLedgerTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : reconstruit le schéma zod traduit à chaque changement de langue.
  const schema = useMemo(() => createProviderSchema(), [i18n.language]);

  const create = useCreateProvider();
  const update = useUpdateProvider();
  const pending = create.isPending || update.isPending;
  const [preview, setPreview] = useState<ProviderDto | null>(null);

  const form = useForm({
    defaultValues: provider
      ? {
          code: provider.code,
          name: provider.name,
          type: provider.type,
          status: provider.status ?? "ACTIVE",
        }
      : EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  const handleConfirm = () => {
    if (!preview) return;
    if (provider) {
      update.mutate(
        { providerId: provider.id, body: preview },
        {
          onSuccess: () => {
            notifSilence(t("ledger.providers.feedback.updated"));
            setPreview(null);
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        }
      );
    } else {
      create.mutate(preview, {
        onSuccess: () => {
          notifSilence(t("ledger.providers.feedback.created"));
          setPreview(null);
          onOpenChange(false);
        },
        onError: (error) => notifSilenceError(error.message),
      });
    }
  };

  const fieldError =
    (name: keyof ProviderFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {provider
                ? t("ledger.providers.edit")
                : t("ledger.providers.create")}
            </DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="code"
              validators={{ onChange: fieldError("code") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="code">{t("ledger.providers.form.code")}</Label>
                  <Input
                    id="code"
                    className="notranslate"
                    placeholder={t("ledger.providers.form.codePlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="name"
              validators={{ onChange: fieldError("name") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="name">{t("ledger.providers.form.name")}</Label>
                  <Input
                    id="name"
                    className="notranslate"
                    placeholder={t("ledger.providers.form.namePlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="type">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("ledger.providers.form.type")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(v as ProviderFormValues["type"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROVIDER_TYPES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="status">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("ledger.providers.form.status")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(v as ProviderFormValues["status"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROVIDER_STATUSES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={preview}
          title={
            provider
              ? t("ledger.providers.preview.updateTitle")
              : t("ledger.providers.preview.createTitle")
          }
          description={t("ledger.providers.preview.description")}
          mode="user"
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !pending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t,
            confirmLoading: pending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}
